import React, { useState, useEffect, useRef } from 'react';
import { DateRangePicker, defaultStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { FaCalendarAlt } from 'react-icons/fa';
import { CButton } from '@coreui/react';

/**
 * Formats a Date object into a string representation.
 * Default format: MM/DD/YYYY
 */
export const formatDateString = (dateInput, outputFormat = 'MM/DD/YYYY') => {
  if (!dateInput) return '';
  const dateInstance = new Date(dateInput);
  if (isNaN(dateInstance.getTime())) return String(dateInput);

  const year = dateInstance.getFullYear();
  const month = String(dateInstance.getMonth() + 1).padStart(2, '0');
  const day = String(dateInstance.getDate()).padStart(2, '0');

  if (outputFormat === 'YYYY-MM-DD') {
    return `${year}-${month}-${day}`;
  }
  return `${month}/${day}/${year}`;
};

/**
 * Parses a range string ("MM/DD/YYYY - MM/DD/YYYY") into Date objects.
 */
export const parseRangeString = (rangeString) => {
  if (!rangeString || typeof rangeString !== 'string' || !rangeString.includes(' - ')) {
    return null;
  }
  const [startStr, endStr] = rangeString.split(' - ');
  const startDate = new Date(startStr);
  const endDate = new Date(endStr);
  if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
    return { startDate, endDate };
  }
  return null;
};

/**
 * Computes initial start and end dates based on input props.
 */
export const computeInitialDates = ({ startDate, endDate, value, defaultToPastWeek }) => {
  if (startDate && endDate) {
    return { start: new Date(startDate), end: new Date(endDate) };
  }
  const parsedRange = parseRangeString(value);
  if (parsedRange) {
    return { start: parsedRange.startDate, end: parsedRange.endDate };
  }
  if (defaultToPastWeek) {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);
    return { start: oneWeekAgo, end: today };
  }
  const today = new Date();
  return { start: today, end: today };
};

/**
 * Computes minDate and maxDate limits from props.
 */
export const computeMinMaxDates = ({ disablePastDates, disableFutureDates, customMinDate, customMaxDate }) => {
  let minDate = customMinDate;
  if (disablePastDates && !minDate) {
    const todayAtMidnight = new Date();
    todayAtMidnight.setHours(0, 0, 0, 0);
    minDate = todayAtMidnight;
  }

  let maxDate = customMaxDate;
  if (disableFutureDates && !maxDate) {
    maxDate = new Date();
  }

  return { minDate, maxDate };
};

/**
 * Computes static preset ranges (filters out "This Month" and "Last Month" if disableMonthPresets is true).
 */
export const computeStaticRanges = (disableMonthPresets = true, customStaticRanges) => {
  if (customStaticRanges) {
    return customStaticRanges;
  }
  if (!disableMonthPresets) {
    return defaultStaticRanges;
  }
  return defaultStaticRanges.filter((staticRange) => {
    const label = staticRange.label ? staticRange.label.toLowerCase() : '';
    return !label.includes('month');
  });
};

/**
 * Clamps date range selection to a maxDaysLimit span.
 */
export const clampSelectionToMaxDays = (selection, maxDaysLimit, maxDate) => {
  if (!selection.startDate || !selection.endDate || !maxDaysLimit) {
    return selection;
  }
  const start = new Date(selection.startDate);
  const end = new Date(selection.endDate);
  const diffInMs = end.getTime() - start.getTime();
  const selectedSpanInDays = Math.round(diffInMs / (1000 * 3600 * 24));

  if (selectedSpanInDays > maxDaysLimit) {
    const cappedEnd = new Date(start);
    cappedEnd.setDate(start.getDate() + maxDaysLimit);

    return {
      ...selection,
      endDate: maxDate && cappedEnd > maxDate ? new Date(maxDate) : cappedEnd,
    };
  }
  return selection;
};

/**
 * Hook to execute a callback when clicking outside the target ref.
 */
const useClickOutside = (ref, onOutsideClick) => {
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClick();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [ref, onOutsideClick]);
};

/**
 * Renders the input trigger element displaying the current date range.
 */
const DateRangeTrigger = ({ displayLabel, isOpen, onToggle }) => (
  <div
    className="form-control d-flex align-items-center justify-content-between"
    style={{ cursor: 'pointer', backgroundColor: '#fff', borderRadius: '6px', minWidth: '250px' }}
    onClick={onToggle}
    role="button"
    tabIndex={0}
  >
    <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
      <FaCalendarAlt style={{ marginRight: '8px', color: '#555' }} />
      {displayLabel}
    </span>
    <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>
      {isOpen ? '▲' : '▼'}
    </span>
  </div>
);

/**
 * Renders the calendar popover overlay.
 */
const DateRangePopover = ({ selectedRange, onSelectionChange, minDate, maxDate, staticRanges, onClose }) => (
  <div
    style={{
      position: 'absolute',
      top: '100%',
      left: 0,
      zIndex: 9999,
      backgroundColor: '#fff',
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
      borderRadius: '8px',
      padding: '12px',
      marginTop: '6px',
    }}
  >
    <DateRangePicker
      ranges={selectedRange}
      onChange={onSelectionChange}
      minDate={minDate}
      maxDate={maxDate}
      staticRanges={staticRanges}
      showSelectionPreview={true}
      moveRangeOnFirstSelection={false}
      editableDateInputs={true}
      direction="horizontal"
      preventSnapRefocus={true}
    />
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
      <CButton color="aimhi-textlight-neutral" size="sm" onClick={onClose}>
        Done
      </CButton>
    </div>
  </div>
);

const DateRangePickerInput = ({
  startDate,
  endDate,
  value,
  onChange,
  disableFutureDates = false,
  disablePastDates = false,
  disableMonthPresets = true,
  minDate: customMinDate,
  maxDate: customMaxDate,
  staticRanges: customStaticRanges,
  maxDays = 7,
  maxRangeDays,
  placeholder = 'Select Date Range',
  defaultToWeekToday = false,
  defaultToPastWeek = false,
  className = '',
  style = {},
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const initialDates = computeInitialDates({
    startDate,
    endDate,
    value,
    defaultToPastWeek: defaultToWeekToday || defaultToPastWeek,
  });

  const [selectedRange, setSelectedRange] = useState([
    {
      startDate: initialDates.start,
      endDate: initialDates.end,
      key: 'selection',
    },
  ]);

  const [draftRange, setDraftRange] = useState(selectedRange);

  const { minDate, maxDate } = computeMinMaxDates({
    disablePastDates,
    disableFutureDates,
    customMinDate,
    customMaxDate,
  });

  const effectiveStaticRanges = computeStaticRanges(disableMonthPresets, customStaticRanges);

  useEffect(() => {
    if (startDate && endDate) {
      const parsedStart = new Date(startDate);
      const parsedEnd = new Date(endDate);
      if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        const newRange = [{ startDate: parsedStart, endDate: parsedEnd, key: 'selection' }];
        setSelectedRange(newRange);
        setDraftRange(newRange);
      }
    } else {
      const parsedRange = parseRangeString(value);
      if (parsedRange) {
        const newRange = [{ startDate: parsedRange.startDate, endDate: parsedRange.endDate, key: 'selection' }];
        setSelectedRange(newRange);
        setDraftRange(newRange);
      }
    }
  }, [value, startDate, endDate]);

  const handleConfirmSelection = () => {
    if (isOpen) {
      const confirmedSelection = draftRange[0] || {};
      setSelectedRange(draftRange);
      setIsOpen(false);

      if (confirmedSelection.startDate && confirmedSelection.endDate && onChange) {
        const formattedStartDate = formatDateString(confirmedSelection.startDate);
        const formattedEndDate = formatDateString(confirmedSelection.endDate);
        const formattedDateRange = `${formattedStartDate} - ${formattedEndDate}`;

        onChange({
          startDate: confirmedSelection.startDate,
          endDate: confirmedSelection.endDate,
          formattedStartDate,
          formattedEndDate,
          startStr: formattedStartDate,
          endStr: formattedEndDate,
          rangeString: formattedDateRange,
          value: formattedDateRange,
        });
      }
    }
  };

  useClickOutside(containerRef, () => {
    if (isOpen) {
      handleConfirmSelection();
    }
  });

  const handleDraftSelectionChange = (pickerRangeItem) => {
    const maxDaysLimit = maxRangeDays || maxDays;
    const clampedSelection = clampSelectionToMaxDays(
      { ...pickerRangeItem.selection },
      maxDaysLimit,
      maxDate
    );

    setDraftRange([clampedSelection]);
  };

  const handleTogglePopover = () => {
    if (!isOpen) {
      setDraftRange(selectedRange);
      setIsOpen(true);
    } else {
      handleConfirmSelection();
    }
  };

  const activeRange = (isOpen ? draftRange[0] : selectedRange[0]) || {};
  const displayLabel =
    activeRange.startDate && activeRange.endDate
      ? `${formatDateString(activeRange.startDate)} - ${formatDateString(activeRange.endDate)}`
      : value || placeholder;

  return (
    <div className={`position-relative ${className}`} ref={containerRef} style={style}>
      <DateRangeTrigger
        displayLabel={displayLabel}
        isOpen={isOpen}
        onToggle={handleTogglePopover}
      />

      {isOpen && (
        <DateRangePopover
          selectedRange={draftRange}
          onSelectionChange={handleDraftSelectionChange}
          minDate={minDate}
          maxDate={maxDate}
          staticRanges={effectiveStaticRanges}
          onClose={handleConfirmSelection}
        />
      )}
    </div>
  );
};

export default DateRangePickerInput;
